/**
 * The one scope of a programme's headline revision that everything describing
 * it reads from.
 *
 * Lifecycle and publishability are both derived per applicability scope, so a
 * badge from one scope beside a control from another puts two answers for one
 * revision side by side. The headline's own scope leads the search, so a
 * revision whose in-force scope can be acted on is acted on there.
 *
 * @module components/portfolio/programme-anchor
 */

import type { PortfolioProgrammeRevision } from "@/platform";

import { manageableScope } from "./CurriculumHistory";
import type { ProgrammeGroup } from "./portfolio-groups";
import { canBePublished } from "./revision-presentation";

export function anchorScope(group: ProgrammeGroup): PortfolioProgrammeRevision {
  return manageableScope([
    group.primary,
    ...group.revisions.filter(
      (row) =>
        row !== group.primary && row.curriculumRevisionId === group.primary.curriculumRevisionId,
    ),
  ]);
}

/** The anchor scope, when the reader may act on it. */
export function manageableAnchor(
  group: ProgrammeGroup,
  access: { canManage: boolean; canPublish: boolean },
): PortfolioProgrammeRevision | null {
  const anchor = anchorScope(group);
  const published = Boolean(anchor.latestPublication && !anchor.latestPublication.withdrawnAt);
  return access.canManage || (access.canPublish && (canBePublished(anchor) || published))
    ? anchor
    : null;
}
